import { defineStore } from "pinia";
import {
  useAuthStore,
  useCrudStore,
  useSchemaStore,
  useUIStore,
  usePurchaseFlowStore,
} from "@/stores";
import { generateRandomId } from "@/utilities";
import { INVESTOR_ACTIVITY_TYPES } from "@/constants";
const API_BASE = import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT;

export const useDataStore = defineStore("dataStore", {
  state: () => ({
    // used to know state has been initialized
    is_app_initialized: false,

    contact: null, // Owner contact
    account: null, // Primary account
    contact: null,
    account: null,
    joint_contacts: [],
    entity_accounts: [],
    beneficial_owner_contacts: [],
    trusted_contacts: [],

    documents: [],
    sub_agreements: [],
    accreditation_documents: [],
    statement: [],

    bank_accounts: [],

    investments: [],
    offerings: [],
    all_offerings: [], // includes non visible offerings for reference in investment details
    distributions: [],
    rollover_details: [],
    exchange_details: [],

    payment_intents: [],

    accepted_file_extensions: [],
    accepted_file_types: [],
  }),
  getters: {
    user_id: () => {
      const authStore = useAuthStore();
      return authStore.current_user?.id;
    },
    owner_full_name: (state) => {
      if (!state.contact) return "";
      return `${state.contact.first_name || ""} ${state.contact.last_name || ""}`.trim();
    },
    s1_offerings_in_quiet_period: (state) => {
      if (!state.offerings) return false;
      return state.offerings
        .filter((offering) => offering.type === "S1")
        .some((offering) => offering.is_quiet_period);
    },
  },
  actions: {
    async setData(tables = null, stay_loading = false) {
      const schemaStore = useSchemaStore();
      const UIStore = useUIStore();

      UIStore.is_loading = true;

      if (!schemaStore.schema_store_is_set) {
        await schemaStore.setSchema();
      }

      const table_configs = this.getTableConfigs();
      const all_tables = Object.keys(table_configs);

      //   console.log("All Tables:", all_tables);
      const tables_to_fetch = tables ?? all_tables;

      const invalid_tables = tables_to_fetch.filter((t) => !table_configs[t]);
      if (invalid_tables.length) {
        console.warn(`Unknown tables requested: ${invalid_tables.join(", ")}`);
      }

      const valid_tables = tables_to_fetch.filter((t) => table_configs[t]);

      try {
        const fetch_promises = valid_tables.map(async (table) => {
          const config = table_configs[table];

          const filters = config.default_filters();
          const options = config.fetch_options ?? {};

          try {
            const data = await config.fetch(filters, options);
            return { table, data, success: true };
          } catch (error) {
            return { table, error, success: false };
          }
        });

        const results = await Promise.all(fetch_promises);

        // Process results in order
        const processing_order = [
          "accounts",
          "contacts",
          "documents",
          "offerings",
          "investments",
          "bankaccounts",
          "rollover_details",
          "settings",
          "trusted_contacts",
        ];

        let sorted_tables = sortByProcessingOrder(valid_tables, processing_order);

        for (const table of sorted_tables) {
          const result = results.find((r) => r.table === table);
          if (result) {
            if (result.success) {
              table_configs[result.table].process(result.data);
            } else {
              console.error(`Error fetching ${result.table}:`, result.error);
            }
          }
        }

        if (valid_tables.includes("investments")) {
          await this.fetchPaymentIntentStatuses();
        }

        this.is_app_initialized = true;
      } catch (err) {
        console.error(`Error in setData: ${err}`);
      } finally {
        if (!stay_loading) {
          UIStore.is_loading = false;
        }
      }
    },
    getTableConfigs() {
      return {
        accounts: {
          default_filters: () => [{ field_name: "user_id", operator: "eq", value: this.user_id }],
          fetch: (filters) => this.fetchTable("accounts", filters),
          process: (data) => this.processAccounts(data),
        },
        contacts: {
          default_filters: () => [{ field_name: "user_id", operator: "eq", value: this.user_id }],
          fetch: (filters) => this.fetchTable("contacts", filters),
          process: (data) => this.processContacts(data),
        },
        trusted_contacts: {
          default_filters: () => [{ field_name: "user_id", operator: "eq", value: this.user_id }],
          fetch: (filters) => this.fetchTable("trustedcontacts", filters),
          process: (data) => this.processTrustedContacts(data),
        },
        documents: {
          default_filters: () => [
            { field_name: "user_id", operator: "eq", value: this.user_id },
            { field_name: "r2_key", operator: "not.in", value: [null, ""] },
            {
              $or: [
                { field_name: "file_category", operator: "is", value: null },
                {
                  $and: [
                    { field_name: "file_category", operator: "neq", value: "Concentration Form" },
                    { field_name: "file_category", operator: "neq", value: "Reg BI" },
                  ],
                },
              ],
            },
          ],
          fetch_options: { sort: "created_date", sort_ascending: false },
          fetch: (filters, options) => this.fetchTable("documents", filters, options),
          process: (data) => this.processDocuments(data),
        },

        investments: {
          default_filters: () => [{ field_name: "user_id", operator: "eq", value: this.user_id }],
          fetch: (filters) => this.fetchTable("investments", filters),
          process: (data) => this.processInvestments(data),
        },
        distributions: {
          default_filters: () => [{ field_name: "user_id", operator: "eq", value: this.user_id }],
          fetch: (filters) => this.fetchTable("distributions", filters),
          process: (data) => (this.distributions = data),
        },
        bankaccounts: {
          default_filters: () => [{ field_name: "user_id", operator: "eq", value: this.user_id }],
          fetch: (filters) => this.fetchTable("bankaccounts", filters),
          process: (data) =>
            (this.bank_accounts = data.sort(
              (a, b) => new Date(b.created_date) - new Date(a.created_date)
            )),
        },
        offerings: {
          default_filters: () => [],
          fetch: (filters) => this.fetchTable("offerings", filters),
          process: (data) => this.processOfferings(data),
        },
        rollover_details: {
          default_filters: () => [{ field_name: "user_id", operator: "eq", value: this.user_id }],
          fetch: (filters) => this.fetchTable("rolloverdetails", filters),
          process: (data) => (this.rollover_details = data),
        },
        exchange_details: {
          default_filters: () => [{ field_name: "user_id", operator: "eq", value: this.user_id }],
          fetch: (filters) => this.fetchTable("exchangenotes", filters),
          process: (data) => (this.exchange_details = data),
        },
        settings: {
          default_filters: () => [
            {
              field_name: "reference",
              operator: "in",
              value: ["accepted_file_types", "accepted_file_extensions"],
            },
          ],
          fetch: (filters) => this.fetchTable("settings", filters),
          process: (data) => {
            this.accepted_file_types =
              data.find((s) => s.reference === "accepted_file_types")?.options || [];
            this.accepted_file_extensions =
              data.find((s) => s.reference === "accepted_file_extensions")?.options || [];
          },
        },
      };
    },
    async fetchTable(table_name, filters, options = {}) {
      const crudStore = useCrudStore();
      const { sort = null, sort_ascending = true } = options;

      return crudStore.find(
        table_name,
        filters,
        null, // projection
        sort, // sort
        null, // range start
        null, // range end
        null, // limit
        sort_ascending // sort order
      );
    },
    processAccounts(data) {
      if (!data || !data.length) {
        this.account = null;
        this.entity_accounts = [];
        return;
      }

      const primaryAccount = data.find((acc) => acc.type === "Regular");
      const entityAccounts = data.filter((acc) => acc.type === "Entity");

      if (primaryAccount) {
        this.account = primaryAccount;
      }

      if (entityAccounts.length > 0) {
        this.entity_accounts = entityAccounts;
      }

      //console.log("Processed Primary Accounts:", this.account);
      // console.log("Processed Entity Accounts:", this.entity_accounts);
    },
    processContacts(data) {
      if (!data || !data.length) {
        this.contact = null;
        this.joint_contacts = [];
        this.beneficial_owner_contacts = [];
        return;
      }

      const ownerContact = data.find((c) => c.type === "Regular");
      const jointContacts = data.filter((c) => c.type === "Joint");
      const beneficialOwnerContacts = data.filter((contact) => {
        return contact.type != "Regular";
      });
      if (ownerContact) {
        this.contact = ownerContact;
      }

      if (jointContacts.length > 0) {
        this.joint_contacts = jointContacts;
      }

      if (beneficialOwnerContacts.length > 0) {
        this.beneficial_owner_contacts = beneficialOwnerContacts;
      }

      // console.log("Processed Owner Contact:", this.contact);
      // console.log("Processed Joint Contacts:", this.joint_contacts);
      // console.log("Processed Beneficial Owner Contacts:", this.beneficial_owner_contacts);
    },
    processDocuments(data) {
      if (!data || !data.length) {
        this.documents = [];
        this.sub_agreements = [];
        this.accreditation_documents = [];
        return;
      }

      for (let doc of data) {
        if (doc.file_type == "application/pdf") {
          doc.formatted_file_type = "PDF";
        } else if (
          doc.file_type == "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ) {
          doc.formatted_file_type = "Word Document";
        } else {
          doc.formatted_file_type = doc.file_type;
        }
      }

      const general_docs = data.filter(
        (doc) => doc.type == "General Documents" || doc.type === "Accreditation"
      );

      // console.log("Statement Data:", data);

      const statement = data.filter((doc) => doc.type === "Statements")[0];

      const sub_docs = data.filter((doc) => doc.type === "Sub Agreements");

      this.documents = general_docs;
      this.statement = statement;
      this.sub_agreements = sub_docs;
      this.accreditation_documents = general_docs.filter((doc) => doc.type === "Accreditation");
      // console.log("Processed General Documents:", this.documents);
      // console.log("Processed Subscription Agreements:", this.sub_agreements);
    },
    processTrustedContacts(data) {
      if (!data || !data.length) {
        this.trusted_contacts = [];
        return;
      }

      this.trusted_contacts = data;
      //console.log("Processed Trusted Contacts:", this.trusted_contacts);
    },
    processInvestments(data) {
      if (!data || !data.length) {
        this.investments = [];
        return;
      }

      // loop through investments and if investment.payment_method is card and investment.funding_account starts with 'pi_', push pi values to new array pi_ids
      const pi_ids = data
        .filter((inv) => inv.payment_method === "Card" && inv.funding_account?.startsWith("pi_"))
        .map((inv) => inv.funding_account);

      //  console.log("Payment Intent IDs for Card Investments:", pi_ids);

      const purchaseFlowStore = usePurchaseFlowStore();

      const investmentsWithSubAndOfferingData = data.map((inv) => {
        let sub_agreement = null;
        let offering = null;
        if (this.sub_agreements) {
          sub_agreement = this.sub_agreements.find((doc) => doc.investment_id === inv.record_id);
        }

        if (this.all_offerings) {
          offering = this.all_offerings.find((off) => off.record_id === inv.offering_id);
        }

        return {
          ...inv,
          sub_agreement,
          bond_term: offering?.bond_term,
          interest_rate: offering?.interest_rate,
        };
      });

      this.investments = investmentsWithSubAndOfferingData;

      // Initialize purchase flow with unsigned investment if one exists
      const unsignedInvestment = investmentsWithSubAndOfferingData.find(
        (inv) => !inv.signature_date && !["Pending", "Pending S1", "Canceled"].includes(inv.status)
      );

      if (unsignedInvestment && !purchaseFlowStore.current_investment.record_id) {
        purchaseFlowStore.current_investment = {
          ...purchaseFlowStore.current_investment,
          ...unsignedInvestment,
        };

        const offering = this.all_offerings?.find(
          (o) => o.record_id === unsignedInvestment.offering_id
        );
        if (offering) {
          purchaseFlowStore.selected_offering = offering;
        }
      }
    },
    async fetchPaymentIntentStatuses() {
      const authStore = useAuthStore();

      const pi_ids = this.investments
        .filter((inv) => inv.payment_method === "Card" && inv.funding_account?.startsWith("pi_"))
        .map((inv) => inv.funding_account);

      if (!pi_ids.length) {
        this.payment_intents = [];
        return;
      }

      const results = await Promise.all(
        pi_ids.map(async (piId) => {
          const res = await fetch(`${API_BASE}/checkout/payment-intent/${piId}/status`, {
            headers: {
              user_id: authStore.current_user.id,
              session_id: authStore.current_user.session_id,
            },
          });
          const data = await res.json();
          return {
            pi_id: piId,
            capture_method: data?.capture_method,
            cancellation_reason: data?.cancellation_reason,
            status: data?.status,
            card_brand: data?.payment_method?.card?.brand,
            last4: data?.payment_method?.card?.last4,
            card_type: data?.payment_method?.card?.funding,
            payment_method_type: data?.payment_method?.type,
            // ...data,
          };
        })
      );

      this.payment_intents = results;
      // console.log("Fetched Payment Intent Statuses:", this.payment_intents);
    },
    processOfferings(data) {
      if (!data || !data.length) {
        this.offerings = [];
        this.all_offerings = [];
        return;
      }

      this.offerings = data.filter(
        (offering) => offering.is_visible_to_investors && offering.issuer === "PCG Holdings"
      );
      this.all_offerings = data;
      // console.log("Processed Offerings:", this.offerings);
    },
    setDefaultFields(record, table) {
      const authStore = useAuthStore();

      record.record_id = generateRandomId();
      record.user_id = this.user_id;
      record.account_id = this.account?.record_id || null;
      record.created_date = new Date();
      record.created_by_id = this.user_id;
      record.created_by_name = this.owner_full_name;
      record.updated_date = new Date();
      record.updated_by_id = this.user_id;
      record.updated_by_name = this.owner_full_name;
      if (table !== "contacts" || (table === "contacts" && record?.type !== "Regular")) {
        record.contact_id = this.contact?.record_id || null;
      }
      if (table == "investoractivity") {
        record.session_id = authStore.current_user?.session_id || null;
      }
      return record;
    },
    async logInvestorActivity(options = {}) {
      try {
        const purchaseFlowStore = usePurchaseFlowStore();
        const crudStore = useCrudStore();

        const { type, step, substep, message, status, validationResult, details } = options;

        const resolvedStep = step || purchaseFlowStore.currentStep?.name || null;

        const resolvedSubstep =
          substep ||
          purchaseFlowStore.currentStep?.sub_steps?.[purchaseFlowStore.current_substep - 1] ||
          "";

        const typeConfig = INVESTOR_ACTIVITY_TYPES[type] || {};
        // console.log("typeConfig", typeConfig);
        // console.log("type", type);
        const resolvedStatus = status || typeConfig.status || type || "Unknown";
        // console.log("resolvedStatus", resolvedStatus);
        // console.log("resolvedStep", resolvedStep);
        const resolvedMessage =
          message || typeConfig.defaultMessage?.(resolvedStep, resolvedSubstep, details) || "";

        let validationDetails = null;
        if (validationResult && !validationResult.proceed) {
          validationDetails = {
            invalid_fields: validationResult.invalid_fields || [],
            invalid_groups: validationResult.invalid_groups || [],
            error_messages: validationResult.error_messages || [],
            field_errors: validationResult.field_errors || {},
          };
        }

        let record = {
          investment_id:
            purchaseFlowStore.current_investment?.record_id || "investment details id?" || null,
          step: resolvedStep,
          substep: resolvedSubstep,
          status: resolvedStatus,
          message: resolvedMessage,
          url: window.location.pathname + window.location.search,
          validation_details: validationDetails ? JSON.stringify(validationDetails) : null,
        };
        record = this.setDefaultFields(record, "investoractivity");
        // console.log("Logging Investor Activity:", record);
        await crudStore.insert("investoractivity", [record]);
      } catch (error) {
        console.error("[investorActivity] Failed to log activity:", error);
      }
    },
  },
});

// sorts items based on processing order array, with any extra items at the end in original order
function sortByProcessingOrder(items, processingOrder) {
  const orderIndex = new Map(processingOrder.map((key, i) => [key, i]));

  return [...items].sort((a, b) => {
    const aIdx = orderIndex.get(a);
    const bIdx = orderIndex.get(b);

    if (aIdx !== undefined && bIdx !== undefined) {
      return aIdx - bIdx;
    }
    if (aIdx !== undefined) return -1;
    if (bIdx !== undefined) return 1;

    return 0;
  });
}
